/*
Description: This is the file that will handle interacting with the IntakeQ EMR
Date: 06/30/2025
Author: ComprehendAI Team

Notes:
    All Injectors need the following functions:
        getQuestions
        answerQuestions 
        answerQuestion
        getPageInfo
        getPatientInfo
        getLogFromInjector

IntakeQ Specific Notes:
    IntakeQ forms primarily use text areas and input fields for form data collection.
    The forms are typically structured with question labels and corresponding input elements.
*/

const DEFAULT_DOB = '01/01/2000';

/** Main Functions */
export function getPageInfo() {
  const baseURL = window.location.origin;
  let noteType = 'Default';
  const titleInput = getNoteRoot().querySelector(
    'input[ng-model="intake.Questionnaire.Name"]'
  ) as HTMLInputElement;
  if (titleInput && titleInput.value) {
    noteType = titleInput?.value?.trim() || noteType;
  }
  return {
    URL: baseURL,
    'Note Type': noteType
  };
}

function isShown(el: Element | null): boolean {
  return (
    !!el &&
    el.getClientRects().length > 0 &&
    getComputedStyle(el).visibility !== 'hidden'
  );
}

// Pop-up (cd-panel) note when open, otherwise the full-screen page
function getNoteRoot(): ParentNode {
  const panelNote = Array.from(
    document.querySelectorAll(
      '.cd-panel-container [ng-controller="noteController"]'
    )
  ).find(isShown);
  return panelNote || document;
}

function getOwnText(el: Element | null): string {
  if (!el) return '';
  return Array.from(el.childNodes)
    .filter((n) => n.nodeType === Node.TEXT_NODE)
    .map((n) => n.textContent)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeDob(value: string): string {
  const m = value.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (!m) return '';
  return `${m[1].padStart(2, '0')}/${m[2].padStart(2, '0')}/${m[3]}`;
}

/**
 * Name/DOB come from "<name><span> - M/D/YYYY - age N</span>":
 * pop-up: .note-appointment strong; full screen: #toolbar unselect() link.
 */
export function getPatientInfo() {
  const root = getNoteRoot();
  const nameEl =
    root === document
      ? Array.from(document.querySelectorAll('#toolbar a')).find(
          (a) =>
            a.getAttribute('ng-click') === 'unselect()' &&
            a.textContent?.trim()
        ) || null
      : root.querySelector('.note-appointment strong');

  const fullName = getOwnText(nameEl);
  const [firstName = '', ...rest] = fullName.split(/\s+/);
  const lastName = rest.join(' ');
  const emrID = [firstName, lastName].filter(Boolean).join('');
  const dobInput = document.querySelector(
    'input[name="patientDOB"]'
  ) as HTMLInputElement | null;
  const dob =
    normalizeDob(nameEl?.querySelector('span')?.textContent || '') ||
    dobInput?.value?.trim() ||
    DEFAULT_DOB;
  return {
    'EMR ID': emrID,
    'First Name': firstName,
    'Last Name': lastName,
    DOB: dob
  };
}

// Helper function to set React input component values
function setReactInputComponent(input: HTMLInputElement, value: string) {
  const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype,
    'value'
  )?.set;
  if (nativeInputValueSetter) {
    nativeInputValueSetter.call(input, value);
  } else {
    input.value = value;
  }
  input.focus();
  const event = new Event('input', { bubbles: true });
  input.dispatchEvent(event);
  const changeEvent = new Event('change', { bubbles: true });
  input.dispatchEvent(changeEvent);
  input.blur();
}

// Helper function to set textarea values
function setTextareaComponent(textarea: HTMLTextAreaElement, value: string) {
  const nativeTextareaValueSetter = Object.getOwnPropertyDescriptor(
    window.HTMLTextAreaElement.prototype,
    'value'
  )?.set;
  if (nativeTextareaValueSetter) {
    nativeTextareaValueSetter.call(textarea, value);
  } else {
    textarea.value = value;
  }
  textarea.focus();
  const event = new Event('input', { bubbles: true });
  textarea.dispatchEvent(event);
  const changeEvent = new Event('change', { bubbles: true });
  textarea.dispatchEvent(changeEvent);
  textarea.blur();
}

function getQuestionName(q: any): string {
  if (!q) return '';
  if (q instanceof Element) {
    const pre = q.querySelector('h4 pre');
    if (pre && pre.textContent) {
      return pre.textContent.trim();
    }
    return '';
  }
  if (typeof q === 'object' && typeof q.Question === 'string') {
    return q.Question.trim();
  }
  if (typeof q === 'string') {
    return q.trim();
  }
  return '';
}

// Global state
var questions = [];
var questionInteractions = {};

export function getQuestions() {
  questions = [];
  questionInteractions = {};
  const questionContainers = getNoteRoot().querySelectorAll('.question');
  questionContainers.forEach((container) => {
    try {
      // Extract label from <h4><pre>...</pre></h4>
      const pre =
        container.querySelector('h4 pre, h4 > pre, h4')?.querySelector('pre') ||
        container.querySelector('h4')?.querySelector('pre') ||
        null;
      const label = pre && pre.textContent ? pre.textContent.trim() : '';
      if (!label) return;

      const rich = container.querySelector(
        '.redactor-layer[contenteditable="true"]'
      ) as HTMLElement | null;

      const input = rich
        ? null
        : (container.querySelector('textarea, input[type="text"]') as
            | HTMLTextAreaElement
            | HTMLInputElement
            | null);
      // Find the main input/textarea inside the question container
      let existingNote = '';
      if (rich) {
        existingNote = getExistingNote(rich.innerHTML);
      } else if (input) {
        existingNote = getExistingNote(input.value);
      }

      const questionObj = {
        Key: label,
        Question: label,
        Type: 'Long Answer',
        ExistingNote: existingNote
      };
      questions.push(questionObj);
      questionInteractions[label] = {
        Type: 'Long Answer',
        ExistingNote: existingNote,
        Input: (val: string, replyType: string) => {
          if (isEmptyOrNull(val)) {
            return;
          }
          if (rich) {
            setRedactorContent(rich, val, replyType);
            return;
          }

          if (input) {
            if (input instanceof HTMLTextAreaElement) {
              if (replyType === 'append' && input.value) {
                setTextareaComponent(input, input.value + '\n' + val);
              } else {
                setTextareaComponent(input, val);
              }
            } else if (input instanceof HTMLInputElement) {
              if (replyType === 'append' && input.value) {
                setReactInputComponent(input, input.value + '\n' + val);
              } else {
                setReactInputComponent(input, val);
              }
            }
          } else {
            // Try to find a redactor-layer if no input/textarea found
            const redactor = container.querySelector(
              '.redactor-layer[contenteditable="true"]'
            );
            if (redactor) {
              setRedactorContent(redactor as HTMLElement, val, replyType);
            }
          }
        }
      };
      questionInteractions[label.toLowerCase()] = questionInteractions[label];
    } catch (e) {
      console.error('Error processing question container:', e);
    }
  });
  return questions;
}

function isEmptyOrNull(value) {
  return (
    value === null || value === undefined || value === '' || value === 'None'
  );
}

function setRedactorContent(
  richEl: HTMLElement,
  value: string,
  replyType: string
) {
  if (isEmptyOrNull(value)) {
    return;
  }
  if (!/<[a-z][\s\S]*>/i.test(value)) {
    value = value.replace(/\n/g, '<br>');
  }
  // 1. Write into the visible editor
  if (replyType === 'append' && richEl.innerHTML.trim()) {
    richEl.innerHTML += '<br>' + value;
  } else {
    richEl.innerHTML = value;
  }

  // 2. Sync the hidden <textarea> so Angular’s model updates
  const hiddenTa = richEl.closest('.redactor-box')?.querySelector('textarea');
  if (hiddenTa) {
    const descriptor = Object.getOwnPropertyDescriptor(
      window.HTMLTextAreaElement.prototype,
      'value'
    );
    const setter = descriptor?.set;
    if (setter) {
      setter.call(hiddenTa, richEl.innerHTML.replace(/<br>/g, '\n'));
      ['input', 'change'].forEach((evt) =>
        hiddenTa.dispatchEvent(new Event(evt, { bubbles: true }))
      );
    } else {
      console.error('Setter for textarea value is not available.');
    }
  }

  // 3. Let Redactor / Angular know the contenteditable changed
  ['input', 'keyup', 'change'].forEach((evt) =>
    richEl.dispatchEvent(new Event(evt, { bubbles: true }))
  );
}

export function answerQuestions(answers) {
  /*
        Description: This function will take the answers and populate the questions
        
        Args: 
            answers: A dictionary of answers
                "Question": "The question in question"
                "Answer": "The answer to the question"
       
        Return: A list of any questions that were not answered, perhaps the answer didn't match the questions expected or whatnot...
            [
                {
                    Question: "Where was the injury?", 
                    Answer: "Back" 
                    Error: "The error message" (Choice not found, etc)
                },
                ...
            }
    
        */
  var errors = [];
  answers.forEach((answer) => {
    var error = answerQuestion(
      answer['Question'],
      answer['Answer'],
      answer['Reply Type']
    );
    if (error) {
      errors.push({
        Question: answer['Question'],
        Answer: answer['Answer'],
        Error: error
      });
    }
  });
  console.error('Errors:');
  console.error(errors);
  return errors;
}

export function answerQuestion(question, answer, replyType) {
  /*
    Description: This function will take the answer and populate the question
    
    Args: 
        question: The question in question
        answer: The answer to the question
   
    Return: null if success otherwise a string with the error message
    */

  // console.log("Answering Question: '" + question + "' with '" + answer+"'")

  // Make sure the question exists
  if (questionInteractions[question] == null) {
    console.error('Question not found');
    console.error(question);
    // console.log(questionInteractions)
    // console.log(Object.keys(questionInteractions))

    return 'Question not found';
  }

  // If it is a Long Answer or long answer, just populate the input
  if (questionInteractions[question]['Input']) {
    questionInteractions[question]['Input'](answer, replyType);
    return true;
  }

  // Check if the answer is a list of answers or just a single answer
  if (Array.isArray(answer)) {
    // Choose All
    var notFoundAnswers = [];
    for (var i = 0; i < answer.length; i++) {
      if (answer[i] in questionInteractions[question]['Choices']) {
        console.log('Answer found ' + answer[i]);
        questionInteractions[question]['Choices'][answer[i]](true);
        // sleep(1000).then(() => {
        //     questionInteractions[question][answer[i]]()
        // });
      } else {
        notFoundAnswers.push(answer[i]);
      }
    }
    if (notFoundAnswers.length > 0) {
      console.error('The following answers were not found: ' + notFoundAnswers);
      return 'The following answers were not found: ' + notFoundAnswers;
    } else {
      return true;
    }
  } else {
    // Multiple Choice
    if (answer in questionInteractions[question]['Choices']) {
      questionInteractions[question]['Choices'][answer]();
      return true;
    } else {
      console.error('Answer not found');
      console.log('Question: ' + question);
      console.log('Answer:' + answer);
      console.log(questionInteractions[question]['Choices']);
      return 'Answer not found';
    }
  }
}

function getExistingNote(value: any) {
  if (typeof value !== 'string') return null;
  const val = value?.trim();
  return val === '' ? null : val;
}

export async function getLogFromInjector() {
  try {
    return {
      questions,
      questionInteractions
    };
  } catch (error) {
    return { error: error?.toString?.() || 'Unknown error' };
  }
}
